// # Subscription Session Manager for handling subscription functionality
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import cacheManager from '../cache_manager';

class SubscriptionSessionManager {
  constructor() {
    // # Initialize subscription-related properties
    this._session = null;
    this._contentAccess = new Map();
    this._isAdmin = false;
    this._isTutor = false;
    this._userRole = 'user';
    this._subscriptionCheckCache = new Map();
    this.SUBSCRIPTION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    this._listeners = new Map();
    this._libraryProgress = null;
  }

  // # Setup subscription document listener with enhanced real-time updates
  setupSubscriptionListener(uid) {
    if (this._listeners.has(`subscription_${uid}`)) return;

    console.log(`[SubscriptionSessionManager] Setting up real-time subscription listener for user ${uid}`);
    
    const unsubscribe = onSnapshot(doc(db, 'subscriptions', uid), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const subscriptionData = docSnapshot.data();
        console.log(`[SubscriptionSessionManager] Received subscription update for user ${uid}`);
        
        // # Update cache with new data
        cacheManager.set(`subscription_${uid}`, subscriptionData);
        
        // # Only update session if we have a valid session object
        if (this._session) {
          // # Update session's subscription data
          this._session.subscriptions = subscriptionData.subscriptions || {};
          console.log(`[SubscriptionSessionManager] Updated session with new subscription data`);
          
          // # Clear subscription access caches since data has changed
          this._clearSubscriptionCaches();
        }
      }
    }, (error) => {
      console.error(`[SubscriptionSessionManager] Error in subscription listener:`, error);
    });

    this._listeners.set(`subscription_${uid}`, unsubscribe);
  }

  // # Setup library progress document listener for real-time updates
  setupLibraryProgressListener(uid) {
    if (this._listeners.has(`library_progress_${uid}`)) return;

    console.log(`[SubscriptionSessionManager] Setting up real-time library progress listener for user ${uid}`);
    
    const unsubscribe = onSnapshot(doc(db, 'my_lib_progress', uid), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const progressData = docSnapshot.data();
        console.log(`[SubscriptionSessionManager] Received library progress update for user ${uid}`);
        
        // # Update cache with new data
        cacheManager.set(`library_progress_${uid}`, progressData);
        
        // # Update local reference
        this._libraryProgress = progressData;
      }
    }, (error) => {
      console.error(`[SubscriptionSessionManager] Error in library progress listener:`, error);
    });

    this._listeners.set(`library_progress_${uid}`, unsubscribe);
  }

  // # Clear subscription-related caches when data changes
  _clearSubscriptionCaches() {
    this._subscriptionCheckCache.clear();
    this._contentAccess.clear();
  }

  // # Remove specific subscription listener
  removeListener(uid) {
    const key = `subscription_${uid}`;
    const unsubscribe = this._listeners.get(key);
    
    if (unsubscribe) {
      unsubscribe();
      this._listeners.delete(key);
      console.log(`[SubscriptionSessionManager] Removed subscription listener for user ${uid}`);
    }
    
    // Also remove library progress listener if exists
    const progKey = `library_progress_${uid}`;
    const progUnsubscribe = this._listeners.get(progKey);
    
    if (progUnsubscribe) {
      progUnsubscribe();
      this._listeners.delete(progKey);
      console.log(`[SubscriptionSessionManager] Removed library progress listener for user ${uid}`);
    }
  }

  // # Remove all subscription listeners
  removeAllListeners() {
    this._listeners.forEach((unsubscribe) => unsubscribe());
    this._listeners.clear();
    console.log(`[SubscriptionSessionManager] Removed all subscription listeners`);
  }

  // # Initialize session reference from parent SessionManager
  setSession(session) {
    this._session = session;
    
    // # Update role flags from session
    if (session) {
      this._isAdmin = session.isAdmin || false;
      this._isTutor = session.isTutor || false;
      this._userRole = session.userRole || 'user';
    }
  }

  // # Set admin status for subscription checking
  setAdminStatus(isAdmin) {
    this._isAdmin = isAdmin;
    if (isAdmin) {
      // Clear cache if admin status changes to true
      this._subscriptionCheckCache.clear();
    }
  }

  // # Set tutor status for subscription checking
  setTutorStatus(isTutor) {
    this._isTutor = isTutor;
    if (isTutor) {
      // Clear cache if tutor status changes to true
      this._subscriptionCheckCache.clear();
    }
  }

  // # Set user role for subscription checking
  setUserRole(role) {
    this._userRole = role;
    // Set admin and tutor flags based on role
    this._isAdmin = role === 'admin';
    this._isTutor = role === 'tutor';
    
    // Clear caches when role changes
    this._contentAccess.clear();
    this._subscriptionCheckCache.clear();
  }

  // # Load library progress data
  async loadLibraryProgressData(uid, forceRefresh = false) {
    try {
      console.log('[SubscriptionSessionManager] Loading library progress data');
      const progressData = await cacheManager.initializeLibraryProgressCache(uid, forceRefresh);
      this._libraryProgress = progressData;
      return progressData;
    } catch (error) {
      console.error('Error loading library progress data:', error);
      return {};
    }
  }

  // # Get library progress data
  getLibraryProgressData() {
    return this._libraryProgress || {};
  }

  // # Load subscription data with retry mechanism
  async loadSubscriptionData(uid, forceRefresh = false) {
    try {
      console.log('[SubscriptionSessionManager] Loading subscription data');
      let subscriptionData = null;
      let retries = 0;
      const maxRetries = 3;
      
      while (retries < maxRetries && !subscriptionData) {
        console.log(`[SubscriptionSessionManager] Subscription data load attempt ${retries + 1}/${maxRetries}`);
        subscriptionData = await cacheManager.initializeSubscriptionCache(uid, retries > 0 || forceRefresh);
        
        if (subscriptionData) {
          console.log('[SubscriptionSessionManager] Subscription data loaded successfully');
          return subscriptionData.subscriptions || {};
        }
        
        if (retries < maxRetries - 1) {
          console.log('[SubscriptionSessionManager] Retrying subscription data load after delay');
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        retries++;
      }
      
      if (!subscriptionData) {
        console.log('[SubscriptionSessionManager] Failed to load subscription data after retries');
        return {};
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
      // # Return empty subscriptions object on error
      return {};
    }
  }

  // # Get subscription status
  getSubscriptionStatus() {
    return this._session && this._session.subscriptions ? this._session.subscriptions : {};
  }

  // # Check if user has a specific subscription
  isSubscribed(subscriptionType) {
    // # Admin and tutor always have access to all subscription types
    if ((this._session && this._session.isAdmin) || (this._session && this._session.isTutor)) {
      console.log(`[SubscriptionSessionManager] isSubscribed check for ${subscriptionType}: true (admin/tutor access)`);
      return true;
    }
    
    // # Check if session exists
    if (!this._session || !this._session.subscriptions) {
      console.log(`[SubscriptionSessionManager] isSubscribed check for ${subscriptionType}: false (no session or subscriptions)`);
      return false;
    }
    
    // # Direct subscription check
    const subscription = this._session.subscriptions[subscriptionType];
    let isActive = subscription?.status === 'active';
    
    // # Apply access rules
    if (!isActive) {
      // # Check if user has mentorship subscription that grants access to library
      if (subscriptionType === 'step1_library') {
        const mentorshipSub = this._session.subscriptions['step1_mentorship'];
        isActive = mentorshipSub?.status === 'active';
      } else if (subscriptionType === 'step2_library') {
        const mentorshipSub = this._session.subscriptions['step2_mentorship'];
        isActive = mentorshipSub?.status === 'active';
      }
    }
    
    console.log(`[SubscriptionSessionManager] isSubscribed check for ${subscriptionType}: ${isActive}`);
    
    // # Store result in sessionStorage for quick access
    try {
      sessionStorage.setItem(`subscription_access_${subscriptionType}`, isActive.toString());
    } catch (error) {
      // Ignore sessionStorage errors
    }
    
    return isActive;
  }

  // # Check if user is subscribed to a specific subscription type (internal method)
  _isSubscribed(subscriptionType, session) {
    // # If no session provided, use current session
    const currentSession = session || this._session;
    
    // # Admin and tutor always have access to all subscription types
    if (currentSession && (currentSession.isAdmin || currentSession.isTutor)) {
      return true;
    }
    
    // # Check if session exists
    if (!currentSession || !currentSession.subscriptions) {
      return false;
    }
    
    // # Get the subscription data
    const subscription = currentSession.subscriptions[subscriptionType];
    
    // # Check if subscription exists and is active
    if (!subscription) {
      return false;
    }
    
    // # Check subscription status
    return subscription.status === 'active';
  }

  // # Check if user has access to content
  async checkContentAccess(contentType, uid) {
    // # Admin and tutor always have access
    if (this._isAdmin || this._isTutor) {
      console.log(`[SubscriptionSessionManager] checkContentAccess for ${contentType}: true (admin/tutor access)`);
      return true;
    }
    
    // # Check cache first
    const cacheKey = `${contentType}_${uid}_access`;
    const cachedAccess = this._contentAccess.get(cacheKey);
    
    if (cachedAccess !== undefined) {
      console.log(`[SubscriptionSessionManager] checkContentAccess for ${contentType}: ${cachedAccess} (cached)`);
      return cachedAccess;
    }
    
    // # Check subscription
    const hasAccess = await this.checkUserSubscription(contentType, uid);
    
    // # Cache the result
    this._contentAccess.set(cacheKey, hasAccess);
    
    console.log(`[SubscriptionSessionManager] checkContentAccess for ${contentType}: ${hasAccess}`);
    return hasAccess;
  }

  // # Check if user has subscription for content
  async checkUserSubscription(contentType, uid) {
    // # Admin and tutor always have access
    if (this._isAdmin || this._isTutor) {
      return true;
    }
    
    // # Check cache first
    const cacheKey = `${contentType}_${uid}_subscription`;
    const cachedResult = this._subscriptionCheckCache.get(cacheKey);
    
    if (cachedResult && (Date.now() - cachedResult.timestamp < this.SUBSCRIPTION_CACHE_DURATION)) {
      return cachedResult.hasAccess;
    }
    
    // # Get subscription data
    let hasAccess = false;
    
    // # Map content types to subscription types
    const contentToSubscription = {
      'step1_library': ['step1_library', 'step1_mentorship'],
      'step2_library': ['step2_library', 'step2_mentorship'],
      'step1_mentorship': ['step1_mentorship'],
      'step2_mentorship': ['step2_mentorship']
    };
    
    // # Get required subscription types for this content
    const requiredSubscriptions = contentToSubscription[contentType] || [contentType];
    
    // # Check if user has any of the required subscriptions
    if (this._session && this._session.subscriptions) {
      hasAccess = requiredSubscriptions.some(subType => this._isSubscribed(subType, this._session));
    }
    
    // # Cache the result
    this._subscriptionCheckCache.set(cacheKey, {
      hasAccess,
      timestamp: Date.now()
    });
    
    return hasAccess;
  }

  // # Get cached access
  getCachedAccess(contentType, uid) {
    // # Admin and tutor always have access
    if (this._isAdmin || this._isTutor) {
      return true;
    }
    
    // # Check cache first
    const cacheKey = `${contentType}_${uid}_access`;
    const cachedAccess = this._contentAccess.get(cacheKey);
    
    if (cachedAccess !== undefined) {
      return cachedAccess;
    }
    
    // # Default to true for now (open access)
    return true;
  }

  // # Clear access cache
  clearAccessCache() {
    this._contentAccess.clear();
    this._subscriptionCheckCache.clear();
  }

  // # Clear user access cache
  clearUserAccessCache(uid) {
    // Remove all entries for this user
    for (const key of this._contentAccess.keys()) {
      if (key.includes(uid)) {
        this._contentAccess.delete(key);
      }
    }
  }

  // # Cleanup on destroy
  destroy() {
    // # Remove all subscription listeners
    this.removeAllListeners();
    
    // # Clear caches
    this.clearAccessCache();
    
    // # Reset properties
    this._session = null;
    this._isAdmin = false;
    this._isTutor = false;
    this._userRole = 'user';
    this._libraryProgress = null;
  }
}

// # Create singleton instance
const subscriptionManager = new SubscriptionSessionManager();

// # Export singleton
export default subscriptionManager;
